import React from "react";
import globals from "../Globals";
import requests from "../Auth/Requests";
import api from "../Auth/Api";
import { Page } from "../Layout/PageLayout";

import { navigate } from "../Layout/Router";
import {
    Box,
    Chip,
    Container,
    Paper,
    Typography,
    List,
    ListItemButton,
    ListItemText,
    Button,
    CircularProgress,
    Tooltip
} from "@mui/material";

export default class TitlesPage extends React.Component {
    state = {
        refreshing: false
    }

    onRefresh = () => {
        if (this.state.refreshing) return;

        this.setState({ refreshing: true });

        // Calls backend command `refresh` and expects a TitlesResponse payload
        api.general("Refreshing roles", ["refresh"], { successMessage: "Refresh successful!" })
            .then(response => {
                globals.loginData.titles = response;
                requests.persistLogin();
            })
            .catch(() => {})
            .finally(() => {
                // setState re-renders with the refreshed titles
                this.setState({ refreshing: false });
            });
    }

    onSelectTitle = (titleId) => {
        globals.selectedTitle = titleId;
        localStorage.setItem("selectedTitle", titleId);

        // Home → Overview of that title (the location handler falls back to what the account may open)
        navigate({ title: titleId, page: Page.Home.id, sub: Page.Home.subs.Overview.id, deep: [] });
    };

    // One chip per backend service the title can use (titles[id].services), labelled from the service registry
    // (rolesDefinition.services); "core" is every title's baseline and is not shown. A service the title implements
    // but cannot use (titles[id].disabledServices: a dependency is missing) is a warning chip that says which.
    serviceChips = (info) => {
        const registry = globals.loginData?.titles?.rolesDefinition?.services ?? [];
        const name = (id) => registry.find(s => s.id === id)?.title ?? id;
        const services = (info?.services ?? []).filter(id => id !== "core");
        const disabled = info?.disabledServices ?? [];
        if (services.length === 0 && disabled.length === 0) return null;
        return [
            ...services.map(id => <Chip key={id} component="span" label={name(id)} size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />),
            ...disabled.map(d => (
                <Tooltip key={d.id} title={`disabled: requires ${(d.missing ?? []).map(name).join(" and ")}, which this title does not have`}>
                    <Chip component="span" label={`${name(d.id)} (disabled)`} size="small" variant="outlined" color="warning" sx={{ height: 20, fontSize: 11 }} />
                </Tooltip>
            )),
        ];
    }

    render() {
        const titlesDict = globals.loginData?.titles?.titles ?? {};
        const entries = Object.entries(titlesDict);
        const selected = globals.selectedTitle;

        return (
            <Container maxWidth="md" sx={{ paddingTop: 3, paddingBottom: 3 }}>
                <Paper elevation={1} sx={{ padding: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 2 }}>
                        <Typography variant="h6">Game Selection</Typography>

                        <Button
                            variant="outlined"
                            size="small"
                            onClick={this.onRefresh}
                            disabled={this.state.refreshing}
                            sx={{ marginLeft: "auto" }}
                        >
                            {this.state.refreshing ? <CircularProgress size={16} /> : "Refresh"}
                        </Button>
                    </Box>

                    {entries.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            No titles available.
                        </Typography>
                    ) : (
                        <List dense disablePadding>
                            {entries.map(([titleId, info]) => {
                                const display = (info?.title ?? info?.name ?? titleId).toString();
                                const secondary = info?.description ? info.description.toString() : titleId;
                                const isSelected = selected === titleId;

                                return (
                                    <ListItemButton
                                        key={titleId}
                                        selected={isSelected}
                                        onClick={() => this.onSelectTitle(titleId)}
                                    >
                                        <ListItemText
                                            primary={display}
                                            secondaryTypographyProps={{ component: "div" }}
                                            secondary={
                                                <Box component="span" sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5 }}>
                                                    <span>{secondary}</span>
                                                    {this.serviceChips(info)}
                                                </Box>
                                            }
                                        />
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    )}
                </Paper>
            </Container>
        );
    }
}
